import { useEffect, useMemo, useRef } from 'react'
import { useGLTF } from '@react-three/drei';
import { applyProps, useThree } from '@react-three/fiber'
import { DoubleSide, MeshStandardMaterial } from "three";
import { gsap } from "gsap";

function Car(props) {
  const {
    modelFile,
    suspension,
    color,
    decalColor,
    selectedPaint,
    caliperColor,
    selectedWheelColor,
    boltColor,
    lipSideType,
    lipSideColor,
    smallPartType,
    smallPartColor,
  } = props;
  // local build use: /draco/gltf/
  // wp build use: /wp-content/themes/mozygarage-wp-theme/dist/draco/gltf/
  const { scene, nodes, materials } = useGLTF(modelFile, '/wp-content/themes/mozygarage-wp-theme/dist/draco/gltf/')
  const { gl } = useThree();

  const matCarbonRef = useRef(null);
  const matLipSideRef = useRef(null);
  const matSmallPartRef = useRef(null);

  const bodyNode = useMemo(() => Object.values(nodes).find(item => item.name.includes('_body')), [nodes])

  useEffect(() => {
    bodyNode.children.forEach((ch) => {
      if (ch.isMesh) {
        applyProps(ch, { castShadow: true });
        // console.log(ch.name)
        // applyProps(ch, { visible: false });
      }
    })

    matCarbonRef.current = new MeshStandardMaterial({
      map: materials['Mat_carbon_01'].map,
      color:  materials['Mat_carbon_01'].color,
      side: DoubleSide,
      metalness: materials['Mat_carbon_01'].metalness,
      roughness: materials['Mat_carbon_01'].roughness,
      // clearcoatRoughness: materials['Mat_carbon_01'].clearcoatRoughness,
    });

    matLipSideRef.current = nodes['Mesh550_1'].material.clone();
    nodes['Mesh550_1'].material = matLipSideRef.current.clone();
    // Canards
    nodes['Mesh550'].material = matLipSideRef.current.clone();
    nodes['custom_part_03'].material = matLipSideRef.current.clone();

    matSmallPartRef.current = nodes['custom_part_06'].material.clone();
    matSmallPartRef.current.side = DoubleSide;
    nodes['Mesh536'].material = matSmallPartRef.current.clone();
    nodes['Mesh015'].material = matSmallPartRef.current.clone();
    nodes['custom_part_07'].material = matSmallPartRef.current.clone();
    nodes['custom_part_06'].material = matSmallPartRef.current.clone();
    nodes['custom_part_05'].material = matSmallPartRef.current.clone();

    nodes['ADBK-Logo'].material = nodes['ADBK-Logo'].material.clone();
    nodes['ADBK-Logo001'].material = nodes['ADBK-Logo'].material.clone()
    nodes['Bosserex-Logo001'].material = nodes['ADBK-Logo'].material.clone()
    nodes['Bosserex-Logo002'].material = nodes['ADBK-Logo'].material.clone()
    nodes['TCG_TICAN_GARAGE-Logo'].material = nodes['ADBK-Logo'].material.clone()
    nodes['TCG_TICAN_GARAGE-Logo001'].material = nodes['ADBK-Logo'].material.clone()
    nodes['wwwmozy'].material = nodes['ADBK-Logo'].material.clone()
    nodes['wwwmozy001'].material = nodes['ADBK-Logo'].material.clone()

    // Rear Diffusor MOZY
    nodes['Mesh016'].material = matCarbonRef.current.clone();
    nodes['Mesh016_1'].material = matCarbonRef.current.clone();

    // Plane030 Plane017 Window MOZY
    nodes['Plane030'].material = matCarbonRef.current.clone();
    nodes['Plane017'].material = matCarbonRef.current.clone();

    applyProps(nodes['Mitsu-Logo001'], { visible: false });
    applyProps(nodes['Mitsu-Logo002'], { visible: false });
    applyProps(nodes['Carcult-Logo001'], { visible: false });
    applyProps(nodes['Carcult-Logo'], { visible: false });
  }, [scene, materials, nodes, bodyNode.children, gl.capabilities]);

  useEffect(() => {
    applyProps(materials['Mat_rims'], { color: selectedWheelColor, envMapIntensity: 0.5 })
  }, [materials, selectedWheelColor])

  useEffect(() => {
    gsap.to(bodyNode.position, { y: suspension });
  }, [bodyNode, suspension]);

  useEffect(() => {
    applyProps(materials['Mat_car_glossy'], { color: color })
  }, [materials, color])

  useEffect(() => {
    applyProps(nodes['ADBK-Logo'].material, {
      color: decalColor
    })
    applyProps(nodes['ADBK-Logo001'].material, {
      color: decalColor
    })
    applyProps(nodes['Bosserex-Logo001'].material, {
      color: decalColor
    })
    applyProps(nodes['Bosserex-Logo002'].material, {
      color: decalColor
    })
    applyProps(nodes['TCG_TICAN_GARAGE-Logo'].material, {
      color: decalColor
    })
    applyProps(nodes['TCG_TICAN_GARAGE-Logo001'].material, {
      color: decalColor
    })
    applyProps(nodes['wwwmozy'].material, {
      color: decalColor
    })
    applyProps(nodes['wwwmozy001'].material, {
      color: decalColor
    })
  }, [nodes, decalColor])


  // custom_part_05 custom_part_06 custom_part_07
  useEffect(() => {
    if (smallPartType === 0) {
      // mirror R
      applyProps(nodes['Mesh536'].material, matCarbonRef.current);
      // mirror L
      applyProps(nodes['Mesh015'].material, matCarbonRef.current);
      applyProps(nodes['custom_part_07'].material, matCarbonRef.current);
      applyProps(nodes['custom_part_06'].material, matCarbonRef.current);
      applyProps(nodes['custom_part_05'].material, matCarbonRef.current);
    } else {
      applyProps(nodes['Mesh536'].material, {
        map: null,
        color: smallPartColor
      });
      applyProps(nodes['Mesh015'].material, {
        map: null,
        color: smallPartColor
      });
      applyProps(nodes['custom_part_07'].material, {
        map: null,
        color: smallPartColor
      });
      applyProps(nodes['custom_part_06'].material, {
        map: null,
        color: smallPartColor
      });
      applyProps(nodes['custom_part_05'].material, {
        map: null,
        color: smallPartColor
      });
    }
  }, [nodes, smallPartType, smallPartColor])

  useEffect(() => {
    if (lipSideType === 0) {
      applyProps(nodes['Mesh550'].material, matCarbonRef.current);
      applyProps(nodes['Mesh550_1'].material, matCarbonRef.current);
      applyProps(nodes['custom_part_03'].material, matCarbonRef.current);
    } else {
      applyProps(nodes['Mesh550'].material, {
        map: null,
        color: lipSideColor
      });
      applyProps(nodes['Mesh550_1'].material, {
        map: null,
        color: lipSideColor
      });
      applyProps(nodes['custom_part_03'].material, {
        map: null,
        color: lipSideColor
      });
    }
  }, [nodes, lipSideType, lipSideColor])

  useEffect(() => {
    applyProps(materials["Mat_caliper"], {
      color: caliperColor
    })
    applyProps(materials["Mat_caliper "], {
      color: caliperColor
    })
  }, [materials, caliperColor])

  useEffect(() => {
    applyProps(materials["Mat_bolt"], {
      color: boltColor
    })
  }, [materials, boltColor])

  useEffect(() => {
    // 0 = Gloss
    // 1 = Matte
    if (selectedPaint === 0) {
      applyProps(materials['Mat_car_glossy'], { roughness: 0.20000000298023224, clearcoatRoughness: 0.029999999329447746 })
    } else {
      applyProps(materials['Mat_car_glossy'], { roughness: 0.35, clearcoatRoughness: 0.35 })
    }
  }, [selectedPaint, materials])

  // function onDocumentMouseDown(e) {
  //   e.preventDefault();
  //   const mouseUvPos = getMouseUvPosition();
  //   if (!mouseUvPos) {
  //     return;
  //   }
  //   console.log('mouseUvPos', mouseUvPos)
  // }

  // function onDocumentTouchDown(e) {
  //   if (e.cancelable) {
  //     e.preventDefault();
  //   }
  //   const mouseCursorContainer = document.getElementById('configurator-container');
  //   if (!mouseCursorContainer) {
  //     return;
  //   }
  // };

  // function getMouseUvPosition() {
  //   raycaster.setFromCamera( mouse, camera );
  //   const intersects = raycaster.intersectObjects([
  //     nodes['Mesh550'],
  //     nodes['custom_part_03'],
  //   ]);
  //   console.log('intersects', intersects)
  //   if (intersects.length < 0) {
  //     return null;
  //   }
  //   if (!intersects[0]) {
  //     return null;
  //   }
  //   return intersects[0].object.name;
  //   // eslint-disable-next-line
  // };

  return (
    <group position={[0, -0.2, 0]}>
      <primitive object={scene} />
    </group>
  );
}

export default Car;
